/**
 * PipelineStepper — the multi-agent run pipeline shown above the answer during a
 * run: Intake → Planner → Tools → Workers → Synthesis. Phase states are derived
 * from the live store (status / nodes / cost / synth) into a compact, glanceable
 * progress strip with a thin progress bar, mirroring the run's real stage.
 *
 * Hidden when idle. Each phase resolves to done / active / queued / failed and
 * renders a check, a spinner, or a quiet index — all on the --ma-* token system.
 */
import { Check, Loader2 } from "lucide-react";
import { useMultiAgentStore } from "@/stores/multiAgentStore";
import {
  PIPELINE_PHASES,
  PHASE_SUBLABEL,
  type PhaseStatus,
  type PipelinePhase,
} from "@/constants/multi-agent/pipeline.constants";

type Phase = PipelinePhase;

function colorFor(s: PhaseStatus): string {
  if (s === "done") return "var(--ma-state-ok)";
  if (s === "active") return "var(--ma-accent)";
  if (s === "failed") return "var(--ma-state-err)";
  return "var(--ma-text-muted)";
}

export function PipelineStepper({ variant = "strip" }: { variant?: "strip" | "inline" } = {}) {
  const status = useMultiAgentStore((s) => s.status);
  const nodeCount = useMultiAgentStore((s) => Object.keys(s.nodes).length);
  const workersBusy = useMultiAgentStore((s) =>
    Object.values(s.nodes).some((n) => n.status === "running" || n.status === "tool_calling"),
  );
  const workersAllDone = useMultiAgentStore((s) => {
    const ns = Object.values(s.nodes);
    return ns.length > 0 && ns.every((n) => n.status === "done" || n.status === "failed" || n.status === "skipped");
  });
  const toolCalls = useMultiAgentStore((s) => s.cost?.toolCalls ?? 0);
  const synthLen = useMultiAgentStore((s) => (s.textBuffer["synthesizer"] || "").length);
  const hasFinal = useMultiAgentStore((s) => s.finalMarkdown.trim().length > 0);

  if (status === "idle") return null;

  const terminal = status === "done" || status === "failed" || status === "cancelled";
  const failed = status === "failed";

  const phaseStatus = (phase: Phase): PhaseStatus => {
    switch (phase) {
      case "Intake":
        return "done";
      case "Planner":
        if (status === "planning" || status === "starting") return "active";
        return nodeCount > 0 || terminal ? "done" : "queued";
      case "Tools":
        if (terminal) return failed ? "failed" : "done";
        if (workersBusy || toolCalls > 0) return "active";
        return workersAllDone ? "done" : "queued";
      case "Workers":
        if (terminal) return nodeCount > 0 ? (failed ? "failed" : "done") : "queued";
        if (workersBusy) return "active";
        return workersAllDone ? "done" : "queued";
      case "Synthesis":
        if (hasFinal) return "done";
        if (failed) return "failed";
        if (terminal) return "done";
        return synthLen > 0 ? "active" : "queued";
    }
  };

  const states = PIPELINE_PHASES.map(phaseStatus);
  const doneCount = states.filter((s) => s === "done").length;
  const activeCount = states.filter((s) => s === "active").length;
  const progressPct = Math.min(100, Math.round(((doneCount + activeCount * 0.5) / PIPELINE_PHASES.length) * 100));

  // Compact single-line variant for the header (next to the token meter): just
  // the stage chips — no sublabels, no progress bar.
  if (variant === "inline") {
    return (
      <div className="ma-fade-up flex items-center gap-1.5">
        {PIPELINE_PHASES.map((phase, i) => {
          const s = states[i];
          const color = colorFor(s);
          return (
            <div key={phase} className="flex items-center gap-1 min-w-0">
              <span
                className="inline-flex shrink-0 items-center justify-center"
                style={{
                  width: 22,
                  height: 22,
                  borderRadius: 999,
                  fontSize: 11,
                  fontWeight: 600,
                  color: s === "queued" ? "var(--ma-text-muted)" : "#fff",
                  background: s === "queued" ? "var(--ma-surface-soft)" : color,
                  border: s === "queued" ? "1px solid var(--ma-border)" : "none",
                }}
              >
                {s === "done" ? (
                  <Check className="h-3 w-3" />
                ) : s === "active" ? (
                  <Loader2 className="h-3 w-3 animate-spin" />
                ) : (
                  i + 1
                )}
              </span>
              <span
                className="hidden truncate md:block"
                style={{ fontSize: 11, fontWeight: 500, color: s === "queued" ? "var(--ma-text-muted)" : "var(--ma-text)" }}
              >
                {phase}
              </span>
              {i < PIPELINE_PHASES.length - 1 && (
                <span
                  aria-hidden
                  style={{
                    width: 10,
                    height: 1,
                    background: s === "done" ? "var(--ma-state-ok)" : "var(--ma-border)",
                  }}
                />
              )}
            </div>
          );
        })}
      </div>
    );
  }

  return (
    <div className="ma-fade-up" style={{ padding: "10px 16px 12px" }}>
      <div className="flex items-stretch gap-1">
        {PIPELINE_PHASES.map((phase, i) => {
          const s = states[i];
          const color = colorFor(s);
          return (
            <div key={phase} className="flex flex-1 items-center gap-2 min-w-0">
              <span
                className="inline-flex shrink-0 items-center justify-center"
                style={{
                  width: 22,
                  height: 22,
                  borderRadius: 999,
                  fontSize: 11,
                  fontWeight: 600,
                  color: s === "queued" ? "var(--ma-text-muted)" : "#fff",
                  background:
                    s === "queued"
                      ? "var(--ma-surface-soft)"
                      : color,
                  border: s === "queued" ? "1px solid var(--ma-border)" : "none",
                }}
              >
                {s === "done" ? (
                  <Check className="h-3 w-3" />
                ) : s === "active" ? (
                  <Loader2 className="h-3 w-3 animate-spin" />
                ) : (
                  i + 1
                )}
              </span>
              <span className="min-w-0">
                <span
                  className="block truncate"
                  style={{ fontSize: 12, fontWeight: 500, color: "var(--ma-text)" }}
                >
                  {phase}
                </span>
                <span
                  className="block truncate"
                  style={{ fontSize: 10, color, textTransform: "none", letterSpacing: 0 }}
                >
                  {PHASE_SUBLABEL[s]}
                </span>
              </span>
              {i < PIPELINE_PHASES.length - 1 && (
                <span
                  aria-hidden
                  className="mx-1 hidden flex-1 sm:block"
                  style={{
                    height: 1,
                    background:
                      states[i] === "done"
                        ? "var(--ma-state-ok)"
                        : "var(--ma-border)",
                    minWidth: 12,
                  }}
                />
              )}
            </div>
          );
        })}
      </div>
      <div
        className="mt-2.5"
        style={{ height: 3, borderRadius: 999, background: "var(--ma-surface-soft)", overflow: "hidden" }}
      >
        <div
          style={{
            height: "100%",
            width: `${progressPct}%`,
            borderRadius: 999,
            background: failed ? "var(--ma-state-err)" : "var(--ma-accent)",
            transition: "width 400ms var(--ma-ease, ease)",
          }}
        />
      </div>
    </div>
  );
}
